/**
 * useSettlements — Stories 2.5 + 2.6 (Settlement-Uebersicht + Payout-Sheet)
 *
 * Referenz (AUTORITATIV): claude-design-import/settlements.html.
 *
 * Datenquellen (ausschliesslich generierter api-client + api-types, Welle 2):
 *  - GET /api/v2/settlements        → Liste inkl. meta.totals + meta.counts
 *  - GET /api/v2/settlements/{id}   → Detail fuer das Payout-Sheet
 *                                     (nur hier liefert der Contract
 *                                     calculation_breakdown; payout ist in
 *                                     Liste UND Detail enthalten)
 *  - GET /api/v2/hosts              → Host-Optionen fuer den Filter
 *
 * Filter Monat/Status/Host laufen SERVERSEITIG (Contract-Parameter month/
 * status/host_id der Liste) — kein clientseitiges Nachfiltern.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { components } from '@heliumtracker/api-types'

export type Settlement = components['schemas']['Settlement']
export type SettlementTotal = components['schemas']['SettlementTotal']
export type SettlementStatus = NonNullable<Settlement['status']>
export type SettlementListResponse = components['schemas']['SettlementListResponse']
export type SettlementCounts = NonNullable<NonNullable<SettlementListResponse['meta']>['counts']>
export type PagyMeta = components['schemas']['PagyMeta']

export type SettlementFilters = {
  /** Abrechnungsmonat YYYY-MM — undefined = alle Monate. */
  month?: string
  /** Status-Filter — undefined = alle Status. */
  status?: SettlementStatus
  /** Host-Filter — undefined = alle Hosts. */
  hostId?: number
}

/** Kontrakt-Maximum fuer per_page (siehe openapi.yaml). */
const MAX_PER_PAGE = 100

// --- Liste -------------------------------------------------------------------

export function useSettlements(page: number, filters: SettlementFilters) {
  const query = useApi<SettlementListResponse>(
    [
      'settlements',
      'list',
      page,
      filters.month ?? null,
      filters.status ?? null,
      filters.hostId ?? null,
    ],
    () =>
      apiClient.GET('/api/v2/settlements', {
        params: {
          query: {
            page,
            per_page: 25,
            ...(filters.month ? { month: filters.month } : {}),
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.hostId !== undefined ? { host_id: filters.hostId } : {}),
          },
        },
      }),
  )

  return {
    settlements: query.data?.data,
    pagy: query.data?.meta?.pagy,
    /** Globale Summen je (Waehrung, Status) ueber den gefilterten Scope. */
    totals: query.data?.meta?.totals,
    /**
     * Zaehler je Status ueber den GESAMTEN gefilterten Scope (alle Seiten;
     * der Status-Filter selbst wird serverseitig fuer die Zaehler ignoriert,
     * damit die Hero-Kacheln immer das Gesamtbild zeigen).
     */
    counts: query.data?.meta?.counts,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  }
}

// --- Faellige Abrechnungen (WP12: Sammel-Auszahlung) -------------------------

/**
 * Alle approved Settlements fuer die Sammel-Auszahlung ("Alle fälligen
 * auszahlen"), unabhaengig vom Status-Filter der Seite. Nutzt DENSELBEN
 * Contract-Parameter wie useSettlements (status=approved), aber mit dem
 * Kontrakt-Maximum per_page=100 statt der Seiten-Groesse 25 — die Aktion
 * soll ALLE fälligen des gewaehlten Zeitraums erfassen, nicht nur eine Seite.
 * Bewusst ein eigener Hook (statt useSettlements mit weiterem Argument):
 * verhindert einen zweiten, Status-verfaelschenden Aufruf desselben Hooks
 * neben der Haupttabelle (siehe SettlementsPage-Tests, die den letzten
 * useSettlements-Call auf die Tabellen-Filter pruefen).
 *
 * Bekannte Grenze: bei > 100 gleichzeitig faelligen Settlements in einem
 * Zeitraum werden nur die ersten 100 erfasst (gleiches Kontrakt-Maximum wie
 * useHostOptions) — im Praxis-Rahmen (ein Host pro Standort/Monat) sehr
 * unwahrscheinlich, aber nicht durch einen neuen BE-Endpunkt geloest, da
 * WP12 ausdruecklich KEINEN neuen Endpunkt vorsieht.
 */
export function useDueSettlements(filters: { month?: string; hostId?: number }) {
  const query = useApi<SettlementListResponse>(
    ['settlements', 'due', filters.month ?? null, filters.hostId ?? null],
    () =>
      apiClient.GET('/api/v2/settlements', {
        params: {
          query: {
            page: 1,
            per_page: MAX_PER_PAGE,
            status: 'approved',
            ...(filters.month ? { month: filters.month } : {}),
            ...(filters.hostId !== undefined ? { host_id: filters.hostId } : {}),
          },
        },
      }),
  )

  return {
    settlements: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

// --- Detail (Payout-Sheet) ---------------------------------------------------

type SettlementDetailResponse = components['schemas']['SettlementResponse']

export function useSettlementDetail(id: number | undefined) {
  const query = useApi<SettlementDetailResponse>(
    ['settlements', 'detail', id],
    () =>
      apiClient.GET('/api/v2/settlements/{id}', {
        params: { path: { id: id as number } },
      }),
    { enabled: id !== undefined && Number.isFinite(id) },
  )

  return {
    settlement: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

// --- Host-Optionen (Filter-Dropdown) -----------------------------------------

export type HostOption = { id: number; name: string }

type HostListResponse = {
  data?: components['schemas']['Host'][]
  meta?: { pagy?: PagyMeta }
}

/**
 * Dokumentierte Grenze: per_page=100 (Kontrakt-Maximum) auf Seite 1 — bei
 * mehr als 100 Hosts fehlen Optionen im Dropdown (gleiches Muster wie
 * useHosts, siehe open_issue der Story 2.2).
 */
export function useHostOptions(): { hosts: HostOption[]; isLoading: boolean } {
  const query = useApi<HostListResponse>(['settlements', 'host-options'], () =>
    apiClient.GET('/api/v2/hosts', {
      params: { query: { per_page: MAX_PER_PAGE } },
    }),
  )

  const hosts: HostOption[] = []
  for (const h of query.data?.data ?? []) {
    if (h.id !== undefined) hosts.push({ id: h.id, name: h.name ?? `#${h.id}` })
  }
  return { hosts, isLoading: query.isLoading }
}
